import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { usePermissions } from '../contexts/PermissionContext';
import api, { UserWithPermissions } from '../services/api';
import { Invitation } from '../types/permissions';
import { useNotification } from '../contexts/NotificationContext';
import { useTimezone } from '../contexts/TimezoneContext';
import { formatUiDateTime } from '../utils/dateUtils';

export const UsersView: React.FC = () => {
  const { timezone } = useTimezone();
  const { isAdmin, hasPermission, permissions } = usePermissions();
  const { showNotification } = useNotification();
  const [users, setUsers] = useState<UserWithPermissions[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteMode, setInviteMode] = useState<'invite' | 'assign'>('invite');
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'viewer',
    tenant_id: '',
    message: ''
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserWithPermissions | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [errorInvitations, setErrorInvitations] = useState<string | null>(null);
  const [revokingInvitationId, setRevokingInvitationId] = useState<string | null>(null);
  const pushNotification = useCallback((message: string, severity: 'success' | 'error') => {
    showNotification(message, severity);
  }, [showNotification]);

  // Get partner admin's partner IDs
  const getPartnerAdminPartnerIds = useCallback(() => {
    if (isAdmin) return null; // Global admin sees all users
    return permissions?.partner_permissions
      ?.filter(p => p.role === 'admin')
      ?.map(p => p.partner_id) || [];
  }, [isAdmin, permissions?.partner_permissions]);

  // Filter users based on partner admin scope
  const filterUsersByPartnerScope = useCallback((allUsers: UserWithPermissions[]) => {
    const partnerIds = getPartnerAdminPartnerIds();
    if (!partnerIds || partnerIds.length === 0) return allUsers;

    return allUsers.filter(user => {
      // Include users who have permissions to the partner admin's partners
      const hasPartnerAccess = user.partner_permissions.some(p => 
        partnerIds.includes(p.partner_id)
      );
      
      // Include users who have permissions to tenants under the partner admin's partners
      const hasTenantAccess = user.tenant_permissions.some(t => {
        const tenantPartner = permissions?.tenant_permissions?.find(tp => tp.tenant_id === t.tenant_id);
        return tenantPartner && partnerIds.includes(tenantPartner.partner_id);
      });

      return hasPartnerAccess || hasTenantAccess;
    });
  }, [getPartnerAdminPartnerIds, permissions?.tenant_permissions]);

  const loadUsers = useCallback(async () => {
    if (!isAdmin && !hasPermission('manage_tenant_users')) return;
    
    setLoading(true);
    setError(null);
    try {
      const usersData = await api.getAllUsersWithPermissions();
      const filteredUsers = filterUsersByPartnerScope(usersData);
      setUsers(filteredUsers);
    } catch (err) {
      setError('Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, hasPermission, filterUsersByPartnerScope]);

  const loadTenants = useCallback(async () => {
    try {
      const tenantsData = await api.getTenants();
      // Filter tenants based on partner admin scope
      const partnerIds = getPartnerAdminPartnerIds();
      if (partnerIds && partnerIds.length > 0) {
        const filteredTenants = tenantsData.filter((tenant: any) => 
          tenant.partner_id && partnerIds.includes(tenant.partner_id)
        );
        setTenants(filteredTenants);
      } else {
        setTenants(tenantsData);
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
    }
  }, [getPartnerAdminPartnerIds]);

  const loadInvitations = useCallback(async () => {
    setLoadingInvitations(true);
    setErrorInvitations(null);
    try {
      const data = await api.getInvitations();
      setInvitations(data);
    } catch (err) {
      setErrorInvitations('Failed to load invitations');
      console.error('Error loading invitations:', err);
    } finally {
      setLoadingInvitations(false);
    }
  }, []);

  const handleInviteUser = async () => {
    if (!inviteForm.email || !inviteForm.tenant_id) {
      pushNotification('Please fill in all required fields', 'error');
      return;
    }

    setInviting(true);
    try {
      if (inviteMode === 'invite') {
        // Send invitation email
        await api.inviteUserToTenant(inviteForm.tenant_id, {
          email: inviteForm.email,
          role: inviteForm.role,
          message: inviteForm.message
        });
        pushNotification('Invitation sent successfully', 'success');
      } else {
        // Immediately assign existing user
        await api.assignUserToTenant(inviteForm.tenant_id, {
          email: inviteForm.email,
          role: inviteForm.role
        });
        pushNotification('User assigned to tenant successfully', 'success');
      }
      
      setInviteDialogOpen(false);
      setInviteForm({ email: '', role: 'viewer', tenant_id: '', message: '' });
      loadUsers();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const errorMessage = detail ?? (err instanceof Error ? err.message : 'Failed to process request');
      pushNotification(errorMessage, 'error');
    } finally {
      setInviting(false);
    }
  };

  const handleInviteDialogClose = () => {
    setInviteDialogOpen(false);
    setInviteForm({ email: '', role: 'viewer', tenant_id: '', message: '' });
  };

  const handleDeleteUser = (user: UserWithPermissions) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await api.deleteUser(userToDelete.id);
      pushNotification('User deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      pushNotification(errorMessage, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    setRevokingInvitationId(invitationId);
    try {
      await api.revokeInvitation(invitationId);
      pushNotification('Invitation revoked successfully', 'success');
      loadInvitations();
    } catch (err) {
      pushNotification('Failed to revoke invitation', 'error');
    } finally {
      setRevokingInvitationId(null);
    }
  };

  useEffect(() => {
    loadUsers();
    loadTenants();
    loadInvitations();
  }, [loadUsers, loadTenants, loadInvitations]);

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'operator':
        return 'warning';
      case 'viewer':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTotalPermissions = (user: UserWithPermissions) => {
    return user.partner_permissions.length + user.tenant_permissions.length;
  };

  if (!isAdmin && !hasPermission('manage_tenant_users')) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
          Users Management
        </Typography>
        <Alert severity="info">
          Only administrators or users with user management permissions can view and manage users.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Users Management
        </Typography>
        {(isAdmin || hasPermission('invite_tenant_users')) && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite User to Tenant
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Invitations Section */}
          <Grid item xs={12}>
            <Box mb={4}>
              <Typography variant="h5" gutterBottom>Pending Invitations</Typography>
              {errorInvitations && (
                <Alert severity="error" sx={{ mb: 2 }}>{errorInvitations}</Alert>
              )}
              {loadingInvitations ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : invitations.length === 0 ? (
                <Typography color="text.secondary">No pending invitations.</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Invited By</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell>Expires At</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Revoke</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invitations.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell>{inv.email}</TableCell>
                          <TableCell>{inv.role}</TableCell>
                          <TableCell>{inv.invited_by}</TableCell>
                          <TableCell>{formatUiDateTime(inv.created_at, timezone)}</TableCell>
                          <TableCell>{formatUiDateTime(inv.expires_at, timezone)}</TableCell>
                          <TableCell>
                            {inv.is_accepted ? (
                              <Chip label="Accepted" color="success" size="small" />
                            ) : (
                              <Chip label="Pending" color="warning" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {!inv.is_accepted && (
                              <IconButton
                                aria-label="Revoke Invitation"
                                color="error"
                                onClick={() => handleRevokeInvitation(inv.id)}
                                disabled={revokingInvitationId === inv.id}
                                size="small"
                              >
                                {revokingInvitationId === inv.id ? <CircularProgress size={20} /> : <DeleteIcon />}
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Grid>
          {users.map((user) => (
            <Grid item xs={12} key={user.id}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <PersonIcon color="primary" />
                      <Box>
                        <Typography variant="h6" component="div">
                          {user.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {user.is_admin && (
                        <Chip
                          icon={<AdminIcon />}
                          label="Global Admin"
                          color="error"
                          size="small"
                        />
                      )}
                      <Chip
                        icon={user.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                        label={user.is_active ? 'Active' : 'Inactive'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                      <Badge badgeContent={getTotalPermissions(user)} color="primary">
                        <Chip
                          label="Permissions"
                          size="small"
                          variant="outlined"
                        />
                      </Badge>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteUser(user)}
                        size="small"
                        title="Delete User"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    {/* Partner Permissions */}
                    <Grid item xs={12} md={6}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <BusinessIcon color="primary" />
                            <Typography variant="subtitle1">
                              Partner Permissions ({user.partner_permissions.length})
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {user.partner_permissions.length > 0 ? (
                            <Box display="flex" flexDirection="column" gap={1}>
                              {user.partner_permissions.map((permission) => (
                                <Box
                                  key={permission.partner_id}
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  p={1}
                                  sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}
                                >
                                  <Typography variant="body2">
                                    {permission.partner_name}
                                  </Typography>
                                  <Chip
                                    label={permission.role}
                                    color={getRoleColor(permission.role) as any}
                                    size="small"
                                  />
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No partner permissions
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </Grid>

                    {/* Tenant Permissions */}
                    <Grid item xs={12} md={6}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <HomeIcon color="primary" />
                            <Typography variant="subtitle1">
                              Tenant Permissions ({user.tenant_permissions.length})
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {user.tenant_permissions.length > 0 ? (
                            <Box display="flex" flexDirection="column" gap={1}>
                              {user.tenant_permissions.map((permission) => (
                                <Box
                                  key={`${permission.tenant_id}-${permission.permission_type}`}
                                  display="flex"
                                  flexDirection="column"
                                  p={1}
                                  sx={{ 
                                    border: '1px solid #e0e0e0', 
                                    borderRadius: 1,
                                    backgroundColor: permission.permission_type === 'inherited' ? '#f8f9fa' : 'white'
                                  }}
                                >
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                    <Typography variant="body2" fontWeight="medium">
                                      {permission.tenant_name}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      {permission.permission_type === 'inherited' && (
                                        <Chip
                                          label="Inherited"
                                          color="info"
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                      <Chip
                                        label={permission.role}
                                        color={getRoleColor(permission.role) as any}
                                        size="small"
                                      />
                                    </Box>
                                  </Box>
                                  {permission.permission_type === 'inherited' && (
                                    <Typography variant="caption" color="text.secondary">
                                      Inherited from partner: {permission.partner_name}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No tenant permissions
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onClose={handleInviteDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {inviteMode === 'invite' ? 'Invite User to Tenant' : 'Assign User to Tenant'}
        </DialogTitle>
        <DialogContent>
          {!isAdmin && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {inviteMode === 'invite' 
                ? 'You can invite users to your partner\'s tenants only. The user will receive an email invitation.'
                : 'You can assign existing users to your partner\'s tenants immediately.'
              }
            </Alert>
          )}
          
          {/* Mode Toggle */}
          <Box sx={{ mb: 2 }}>
            <FormControl component="fieldset">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={inviteMode === 'invite'}
                    onChange={(e) => setInviteMode(e.target.checked ? 'invite' : 'assign')}
                  />
                }
                label="Send email invitation (for new users)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={inviteMode === 'assign'}
                    onChange={(e) => setInviteMode(e.target.checked ? 'assign' : 'invite')}
                  />
                }
                label="Assign immediately (for existing users)"
              />
            </FormControl>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={inviteForm.email}
            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Tenant</InputLabel>
            <Select
              value={inviteForm.tenant_id}
              label="Tenant"
              onChange={(e) => setInviteForm({ ...inviteForm, tenant_id: e.target.value })}
            >
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={inviteForm.role}
              label="Role"
              onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
            >
              <MenuItem value="viewer">Viewer</MenuItem>
              <MenuItem value="operator">Operator</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Message (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={inviteForm.message}
            onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
            placeholder="Add a personal message to the invitation..."
            sx={{ display: inviteMode === 'invite' ? 'block' : 'none' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInviteDialogClose} disabled={inviting}>
            Cancel
          </Button>
          <Button 
            onClick={handleInviteUser} 
            variant="contained" 
            disabled={!inviteForm.email || !inviteForm.tenant_id || inviting}
          >
            {inviting ? <CircularProgress size={20} /> : (inviteMode === 'invite' ? 'Send Invitation' : 'Assign User')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm User Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action will permanently delete the user and remove all their permissions. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteUser} 
            variant="contained" 
            color="error"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}; 